import { createFileRoute } from '@tanstack/react-router';
import { useCallback, useEffect, useMemo, type FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SquaredBackground } from '@/components/squared-background';
import { Button } from '@/components/ui/button';
import { buildAndroidAppIntentUrl, isAndroidUserAgent } from '@/utils/mobile-auth-app-link';
import {
  clearCapturedMobileAuthResponse,
  getCapturedMobileAuthResponse,
  stripOAuthParamsFromLocation,
} from '@/utils/scrub-oauth-params';

/**
 * Browser fallback for the mobile app's OAuth redirect target.
 *
 * On a phone with the iPractice app installed this URL is a verified App Link
 * (Android) or Universal Link (iOS), so the OS opens the app and this page is
 * never rendered. It only shows when the link is opened on desktop, or on a
 * device where the app is missing or the domain association has not been
 * verified — which is exactly when the user is stuck and needs a way out.
 *
 * On Android some browsers render this page even though the app is installed:
 * Opera has no Custom Tabs support and keeps the redirect to itself, and Edge
 * only hands it over after a prompt (IP-2339). For those the page offers an
 * "Open the iPractice app" button that passes the sign-in response to the app
 * by explicit package. The response is read from memory, never from the DOM
 * or the address bar, which was scrubbed before telemetry started.
 */
export const MobileAuthFallback: FC = () => {
  const { t, i18n } = useTranslation();

  // B2C redirects here with `?code=…&state=…`. When the App Link is not
  // honoured the browser renders this page, so drop the credential from the
  // address bar before it reaches page-view telemetry or a referrer header.
  // The response is read into `appIntentUrl` on the first render, so clearing
  // it on unmount leaves this page intact and only a later visit without one.
  useEffect(() => {
    stripOAuthParamsFromLocation();
    return clearCapturedMobileAuthResponse;
  }, []);

  const appIntentUrl = useMemo(() => {
    if (!isAndroidUserAgent(navigator.userAgent)) return null;
    const response = getCapturedMobileAuthResponse();
    if (!response) return null;
    return buildAndroidAppIntentUrl({ host: window.location.host, ...response });
  }, []);

  // A button with a navigation, not a link: an `href` would put the code in
  // the DOM, where click-tracking and session replay could pick it up.
  const handleOpenApp = useCallback(() => {
    if (!appIntentUrl) return;
    window.location.assign(appIntentUrl);
  }, [appIntentUrl]);

  const guidance = useMemo(() => {
    if (!appIntentUrl) {
      return <p className="text-muted-foreground">{t('mobile_auth_page.description')}</p>;
    }
    return (
      <div className="space-y-3">
        <p className="text-muted-foreground">{t('mobile_auth_page.openAppHint')}</p>
        <Button size="lg" className="w-full" onClick={handleOpenApp}>
          {t('mobile_auth_page.openAppButton')}
        </Button>
      </div>
    );
  }, [appIntentUrl, handleOpenApp, t]);

  const appStoreUrl = `https://apps.apple.com/nl/app/ipractice/id6737982953?l=${
    i18n.language === 'en' ? 'en-GB' : 'nl-NL'
  }`;
  const playStoreUrl = `https://play.google.com/store/apps/details?id=com.ipractice.myipractice&hl=${i18n.language}`;

  return (
    <div className="min-w-screen min-h-screen flex items-center justify-center relative isolate bg-background px-6">
      <SquaredBackground />
      <div className="relative z-10 w-full max-w-md text-center space-y-6">
        <h1 className="text-2xl font-semibold text-foreground">
          {t('mobile_auth_page.title')}
        </h1>
        {guidance}
        <p className="text-sm text-muted-foreground">{t('mobile_auth_page.getTheApp')}</p>
        <div className="flex items-center justify-center gap-4">
          <a href={appStoreUrl} target="_blank" rel="noreferrer">
            <img src="./appStore.svg" alt={t('mobile_auth_page.appStoreAlt')} className="h-10" />
          </a>
          <a href={playStoreUrl} target="_blank" rel="noreferrer">
            <img src="./playStore.png" alt={t('mobile_auth_page.playStoreAlt')} className="h-10" />
          </a>
        </div>
      </div>
    </div>
  );
};

export const Route = createFileRoute('/mobile-auth')({
  component: MobileAuthFallback,
});
